import { useEffect, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { buscarProdutos, emReais, lerProduto, textoDaFalha } from '../../encomendas';
import { novaConsulta } from '../../faltas';
import type { Bloco, ProdutoAoVivo, ProdutoDaConsulta } from '../../encomendas';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Leitor } from '../balconista/Leitor';
import { ColunaDistribuidoras, ColunaHistorico, ColunaNotas } from './Colunas';
import { BotaoRegistrarFalta } from './RegistrarFalta';
import type { OfertaDaDistribuidora } from '../../encomendas';
import './encomendas.css';
import './fluxo.css';

/**
 * ENCOMENDAS · CONSULTA (fase 2) — "Qual produto você procura?".
 *
 * 1. Busca no catálogo (nome, princípio ativo, marca, código HOS, EAN — ou a câmera).
 * 2. Seleciona o produto: o cabeçalho fixa com o preço e o estoque AO VIVO do HOS.
 * 3. Três colunas, cada uma com a sua fonte, carregando e falhando SOZINHAS: histórico de compras
 *    (InovaPED), notas fiscais (HOS) e disponibilidade agora (InovaPED). No celular viram abas.
 * 4. Duas ações (dono, 02/10): 🔴 Registrar falta (um clique, o cliente procurou e não achou) e
 *    🟢 Encomendar (o cliente quer pedir — leva à distribuidora, o fluxo de sempre).
 */

type Aba = 'distribuidoras' | 'historico' | 'notas';

export function Consulta({
  aoEncomendar,
  registrarFalta,
}: {
  /** Fase 3: abre o formulário da encomenda com o snapshot da oferta. */
  aoEncomendar?: (
    produto: ProdutoDaConsulta,
    aoVivo: ProdutoAoVivo | null,
    oferta: OfertaDaDistribuidora,
  ) => void;
  /** Só para quem tem `encomendas.registrar_falta` (o servidor confere de novo). */
  registrarFalta?: { readonly sessao: Sessao };
}) {
  const [selecionado, setSelecionado] = useState<{
    produto: ProdutoDaConsulta;
    termo: string | null;
    consulta: string;
  } | null>(null);
  return selecionado ? (
    <ProdutoSelecionado
      key={selecionado.consulta}
      produto={selecionado.produto}
      aoTrocar={() => setSelecionado(null)}
      {...(aoEncomendar ? { aoEncomendar } : {})}
      {...(registrarFalta
        ? {
            falta: {
              sessao: registrarFalta.sessao,
              consultaId: selecionado.consulta,
              termo: selecionado.termo,
            },
          }
        : {})}
    />
  ) : (
    // Cada produto aberto = uma consulta nova (um atendimento): é o que separa o clique repetido
    // da procura de outro cliente.
    <Busca
      aoSelecionar={(produto, termo) =>
        setSelecionado({ produto, termo, consulta: novaConsulta() })
      }
    />
  );
}

/* ================================================================== */

function Busca({
  aoSelecionar,
}: {
  aoSelecionar: (p: ProdutoDaConsulta, termo: string | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const [resultado, setResultado] = useState<
    | { fase: 'vazio' }
    | { fase: 'buscando' }
    | { fase: 'pronto'; produtos: readonly ProdutoDaConsulta[]; termo: string }
    | { fase: 'falha'; mensagem: string }
  >({ fase: 'vazio' });
  const [camera, setCamera] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const geracao = useRef(0);

  useEffect(() => entrada.current?.focus(), []);

  useEffect(() => {
    const t = termo.trim();
    if (t.length < 2) {
      setResultado({ fase: 'vazio' });
      return;
    }
    const g = ++geracao.current;
    setResultado((r) => (r.fase === 'pronto' ? r : { fase: 'buscando' }));
    const espera = window.setTimeout(() => {
      void buscarProdutos(t).then((r) => {
        if (g !== geracao.current) return;
        setResultado(
          r.ok
            ? { fase: 'pronto', produtos: r.dados.produtos, termo: t }
            : { fase: 'falha', mensagem: textoDaFalha(r.falha) },
        );
      });
    }, 220);
    return () => window.clearTimeout(espera);
  }, [termo]);

  // Código bipado (leitor do balcão = Enter) ou lido pela câmera: um resultado exato abre direto.
  const abrirSeUnico = (codigo: string) => {
    void buscarProdutos(codigo).then((r) => {
      if (r.ok && r.dados.produtos.length === 1 && r.dados.produtos[0])
        aoSelecionar(r.dados.produtos[0], codigo);
    });
  };

  return (
    <div className="enc-consulta">
      <section className="enc-busca" aria-labelledby="enc-busca-titulo">
        <h1 id="enc-busca-titulo" className="enc-busca-titulo">
          Qual produto você procura?
        </h1>
        <div className="enc-busca-campo">
          <Icone nome="search" tamanho={20} traco={1.7} />
          <input
            ref={entrada}
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && /^\d{6,14}$/.test(termo.trim())) abrirSeUnico(termo.trim());
            }}
            placeholder="Nome, princípio ativo, marca, código HOS ou EAN"
            aria-label="Qual produto você procura?"
            autoComplete="off"
            spellCheck={false}
            inputMode="search"
          />
          {termo && (
            <button
              type="button"
              className="enc-busca-limpar"
              onClick={() => setTermo('')}
              aria-label="Limpar"
            >
              <Icone nome="x" tamanho={16} traco={1.8} />
            </button>
          )}
          <button type="button" className="enc-busca-camera" onClick={() => setCamera(true)}>
            <Icone nome="cam" tamanho={18} traco={1.7} />
            <span>Câmera</span>
          </button>
        </div>
      </section>

      {resultado.fase === 'vazio' && (
        <p className="enc-dica">Digite pelo menos 2 letras — ou bipe o código de barras.</p>
      )}
      {resultado.fase === 'buscando' && <GradeEsqueleto />}
      {resultado.fase === 'falha' && <p className="enc-erro">{resultado.mensagem}</p>}
      {resultado.fase === 'pronto' &&
        (resultado.produtos.length === 0 ? (
          <div className="enc-nada">
            <b>Nenhum produto encontrado para “{resultado.termo}”.</b>
            <span>Confira a grafia ou tente o princípio ativo.</span>
          </div>
        ) : (
          <ul className="enc-grade" aria-label="Produtos encontrados">
            {resultado.produtos.map((p) => (
              <li key={p.id}>
                <CartaoDoProduto
                  produto={p}
                  aoSelecionar={() => aoSelecionar(p, resultado.termo)}
                />
              </li>
            ))}
          </ul>
        ))}

      {camera && (
        <Leitor
          aoLer={(codigo) => {
            setCamera(false);
            setTermo(codigo);
            abrirSeUnico(codigo);
          }}
          aoFechar={() => setCamera(false)}
        />
      )}
    </div>
  );
}

function CartaoDoProduto({
  produto: p,
  aoSelecionar,
}: {
  produto: ProdutoDaConsulta;
  aoSelecionar: () => void;
}) {
  const estoque = p.estoque === null ? null : Number(p.estoque);
  return (
    <article className="enc-cartao">
      <FotoDoProduto url={p.foto} nome={p.descricao} tamanho={76} />
      <div className="enc-cartao-corpo">
        <h3 className="enc-cartao-nome">{p.descricao}</h3>
        <p className="enc-cartao-sub">
          {[p.fabricante, p.principioAtivo].filter(Boolean).join(' · ') ||
            'Fabricante não informado'}
        </p>
        <p className="enc-codigos">
          {p.ean && <span>EAN {p.ean}</span>}
          {p.codigoHos && <span>Cód. {p.codigoHos}</span>}
          {p.exigeReceita && <span className="enc-selo-receita">Exige receita</span>}
        </p>
      </div>
      <div className="enc-cartao-pe">
        <span className={`enc-estoque${estoque !== null && estoque > 0 ? ' enc-estoque-tem' : ''}`}>
          {estoque === null
            ? 'Estoque —'
            : estoque > 0
              ? `${String(estoque)} na loja`
              : 'Sem estoque na loja'}
        </span>
        <span className="enc-cartao-pmc">PMC {emReais(p.pmcCadastro)}</span>
        <button type="button" className="enc-botao" onClick={aoSelecionar}>
          Selecionar
        </button>
      </div>
    </article>
  );
}

function GradeEsqueleto() {
  return (
    <ul className="enc-grade" aria-busy="true" aria-label="Buscando">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <li key={i}>
          <div className="enc-cartao enc-esqueleto">
            <i className="enc-esq-foto" />
            <div className="enc-cartao-corpo">
              <i />
              <i />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ================================================================== */

function ProdutoSelecionado({
  produto,
  aoTrocar,
  aoEncomendar,
  falta,
}: {
  produto: ProdutoDaConsulta;
  aoTrocar: () => void;
  aoEncomendar?: (
    produto: ProdutoDaConsulta,
    aoVivo: ProdutoAoVivo | null,
    oferta: OfertaDaDistribuidora,
  ) => void;
  falta?: { readonly sessao: Sessao; readonly consultaId: string; readonly termo: string | null };
}) {
  const veCusto = useVeCusto();
  const [aoVivo, setAoVivo] = useState<
    | { fase: 'carregando' }
    | { fase: 'pronto'; bloco: Bloco<ProdutoAoVivo | null> }
    | { fase: 'falha'; mensagem: string }
  >({ fase: 'carregando' });
  const [aba, setAba] = useState<Aba>('distribuidoras');

  useEffect(() => {
    let vivo = true;
    void lerProduto(produto.id).then((r) => {
      if (!vivo) return;
      setAoVivo(
        r.ok
          ? { fase: 'pronto', bloco: r.dados.aoVivo }
          : { fase: 'falha', mensagem: textoDaFalha(r.falha) },
      );
    });
    return () => {
      vivo = false;
    };
  }, [produto.id]);

  const vivo = aoVivo.fase === 'pronto' && aoVivo.bloco.ok ? aoVivo.bloco.dados : null;
  const falhaAoVivo =
    aoVivo.fase === 'falha'
      ? aoVivo.mensagem
      : aoVivo.fase === 'pronto' && !aoVivo.bloco.ok
        ? aoVivo.bloco.mensagem
        : null;
  const estoqueLoja = vivo?.estoque ?? (produto.estoque === null ? null : Number(produto.estoque));

  // 🟢 Encomendar (cabeçalho): o fluxo de sempre começa na distribuidora — leva até a coluna e
  // põe o foco na 1ª oferta disponível. Nada muda no formulário da encomenda.
  const irParaDistribuidoras = () => {
    setAba('distribuidoras');
    window.requestAnimationFrame(() => {
      const col = document.querySelector<HTMLElement>('.enc-col-distribuidoras');
      col?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const botao = col?.querySelector<HTMLButtonElement>('.enc-encomendar:not(:disabled)');
      if (botao) botao.focus({ preventScroll: true });
      col?.classList.add('enc-col-destaque');
      window.setTimeout(() => col?.classList.remove('enc-col-destaque'), 1_400);
    });
  };

  return (
    <div className="enc-consulta">
      <header className="enc-produto" aria-label="Produto selecionado">
        <FotoDoProduto url={produto.foto} nome={produto.descricao} tamanho={72} />
        <div className="enc-produto-id">
          <h1 className="enc-produto-nome">{produto.descricao}</h1>
          <p className="enc-cartao-sub">
            {[produto.fabricante, produto.principioAtivo].filter(Boolean).join(' · ') ||
              'Fabricante não informado'}
          </p>
          <p className="enc-codigos">
            {produto.ean && <span>EAN {produto.ean}</span>}
            {produto.codigoHos && <span>Cód. HOS {produto.codigoHos}</span>}
            <span className={produto.ativoNoHos ? 'enc-selo-ok' : 'enc-selo-alerta'}>
              {produto.ativoNoHos ? 'Cadastro ativo' : 'Inativo no HOS'}
            </span>
            {produto.exigeReceita && <span className="enc-selo-receita">Exige receita</span>}
          </p>
        </div>
        <dl className="enc-produto-numeros">
          <div>
            <dt>Estoque da loja</dt>
            <dd className={estoqueLoja !== null && estoqueLoja > 0 ? 'enc-n-ok' : 'enc-n-zero'}>
              {aoVivo.fase === 'carregando' ? (
                <i className="enc-esq-linha" />
              ) : estoqueLoja === null ? (
                '—'
              ) : (
                String(estoqueLoja)
              )}
            </dd>
          </div>
          <div>
            <dt>Preço de venda (HOS)</dt>
            <dd>
              {aoVivo.fase === 'carregando' ? (
                <i className="enc-esq-linha" />
              ) : (
                emReais(vivo?.precoVenda)
              )}
            </dd>
          </div>
          <div>
            <dt>PMC</dt>
            <dd>
              {aoVivo.fase === 'carregando' ? (
                <i className="enc-esq-linha" />
              ) : (
                emReais(vivo?.pmc ?? null)
              )}
            </dd>
          </div>
          {vivo && vivo.descontoAtePct > 0 && (
            <div>
              <dt>Desconto até</dt>
              <dd>{String(vivo.descontoAtePct)}%</dd>
            </div>
          )}
        </dl>
        <button type="button" className="enc-botao-sec enc-trocar" onClick={aoTrocar}>
          <Icone nome="retry" tamanho={15} traco={1.8} /> Trocar produto
        </button>
        {(falta || aoEncomendar) && (
          <div className="enc-produto-acoes">
            <span
              className={`enc-situacao ${
                aoVivo.fase === 'carregando'
                  ? ''
                  : estoqueLoja !== null && estoqueLoja > 0
                    ? 'enc-situacao-tem'
                    : 'enc-situacao-zero'
              }`}
            >
              {aoVivo.fase === 'carregando'
                ? 'Lendo o estoque…'
                : estoqueLoja === null
                  ? 'Estoque não informado'
                  : estoqueLoja > 0
                    ? `HOS diz ${String(estoqueLoja)} na loja — não achou? Registre a falta.`
                    : 'Sem estoque'}
            </span>
            <div className="enc-produto-botoes">
              {falta && (
                <BotaoRegistrarFalta
                  sessao={falta.sessao}
                  produtoId={produto.id}
                  consultaId={falta.consultaId}
                  termoBusca={falta.termo}
                />
              )}
              {aoEncomendar && (
                <button
                  type="button"
                  className="enc-encomendar-verde"
                  onClick={irParaDistribuidoras}
                >
                  <Icone nome="encomendar" tamanho={18} traco={1.8} />
                  Encomendar
                </button>
              )}
            </div>
          </div>
        )}
        {falhaAoVivo && (
          <p className="enc-produto-aviso" role="status">
            Preço e estoque ao vivo indisponíveis: {falhaAoVivo} Mostrando o estoque do último
            espelho do HOS.
          </p>
        )}
      </header>

      <nav className="enc-abas" aria-label="Consulta">
        {(
          [
            ['distribuidoras', 'Disponibilidade agora'],
            ['historico', 'Histórico de compras'],
            ['notas', 'Notas fiscais'],
          ] as const
        ).map(([k, rotulo]) => (
          <button
            key={k}
            type="button"
            className={aba === k ? 'enc-aba-sel' : ''}
            aria-pressed={aba === k}
            onClick={() => setAba(k)}
          >
            {rotulo}
          </button>
        ))}
      </nav>

      <div className="enc-colunas" data-aba={aba}>
        <ColunaHistorico produtoId={produto.id} veCusto={veCusto} />
        <ColunaNotas produtoId={produto.id} veCusto={veCusto} />
        <ColunaDistribuidoras
          produtoId={produto.id}
          veCusto={veCusto}
          {...(aoEncomendar
            ? { aoEncomendar: (o: OfertaDaDistribuidora) => aoEncomendar(produto, vivo, o) }
            : {})}
        />
      </div>
    </div>
  );
}
